import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthBackend, AuthContextValue, AuthUser } from './types';

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ backend, children }: { backend: AuthBackend; children: ReactNode }) {
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await backend.getUser();
      setUser(u);
      setStatus(u ? 'signed-in' : 'signed-out');
    } catch (err) {
      // A signed-in user without a role: treat as signed out but surface the reason.
      console.error(err);
      await backend.signOut().catch(() => undefined);
      setUser(null);
      setStatus('signed-out');
      throw err;
    }
  }, [backend]);

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      refresh,
      async signIn(email, password) {
        const outcome = await backend.signIn(email, password);
        if (outcome.kind === 'done') await refresh();
        return outcome;
      },
      async confirmNewPassword(password) {
        const outcome = await backend.confirmNewPassword(password);
        if (outcome.kind === 'done') await refresh();
        return outcome;
      },
      async confirmTotp(code) {
        const outcome = await backend.confirmTotp(code);
        if (outcome.kind === 'done') await refresh();
        return outcome;
      },
      async signOut() {
        await backend.signOut();
        setUser(null);
        setStatus('signed-out');
      },
      requestPasswordReset: (email) => backend.requestPasswordReset(email),
      confirmPasswordReset: (email, code, pw) => backend.confirmPasswordReset(email, code, pw),
      getIdToken: () => backend.getIdToken(),
      getMfaStatus: () => backend.getMfaStatus(),
      setupTotp: () => backend.setupTotp(),
      verifyTotpSetup: (code) => backend.verifyTotpSetup(code),
      disableTotp: () => backend.disableTotp(),
    }),
    [backend, refresh, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
